"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { createClient } from "@/utils/supabase/client";

import { cn } from "@/lib/utils";

interface AccountProfileCardProps {
  userId: string;
  email?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  myProfileHref: string;
  profileLabel: string;
  profileSub?: string;
  onNavigate?: () => void;
  className?: string;
}

export function AccountProfileCard({
  userId,
  email,
  username,
  avatarUrl: initialAvatarUrl,
  myProfileHref,
  profileLabel,
  profileSub,
  onNavigate,
  className,
}: AccountProfileCardProps) {
  const [avatarUrl, setAvatarUrl] = useState(initialAvatarUrl ?? null);

  useEffect(() => {
    setAvatarUrl(initialAvatarUrl ?? null);
  }, [initialAvatarUrl]);

  useEffect(() => {
    if (avatarUrl?.trim()) return;

    const supabase = createClient();
    void supabase
      .from("profiles")
      .select("avatar_url")
      .eq("id", userId)
      .maybeSingle()
      .then(({ data }) => {
        const next = data?.avatar_url?.trim();
        if (next) setAvatarUrl(next);
      });
  }, [userId, avatarUrl]);

  const resolvedAvatarUrl = getAvatarUrl(avatarUrl);
  const fallbackInitial =
    username?.[0]?.toUpperCase() ?? email?.[0]?.toUpperCase() ?? "U";

  return (
    <Link
      href={myProfileHref}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/40 active:bg-accent/60",
        className,
      )}
    >
      <Avatar className="h-11 w-11 shrink-0">
        <AvatarImage src={resolvedAvatarUrl ?? undefined} alt={profileLabel} />
        <AvatarFallback className="text-sm font-semibold">{fallbackInitial}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[17px] font-bold">{profileLabel}</p>
        {profileSub ? (
          <p className="truncate text-sm text-muted-foreground">{profileSub}</p>
        ) : null}
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
    </Link>
  );
}
